import {
  CASHBACK_COLOR,
  INTEREST_COLOR,
  LOSS_COLOR,
  NET_COLOR,
} from "./constants";
import {
  CompositionSegment,
  CompositionWidths,
  CompositionWidthsParams,
} from "./types";

const cashbackSegmentOf = (
  retainedCashback: number,
  width: number,
): CompositionSegment[] =>
  retainedCashback > 0 ? [{ width, color: CASHBACK_COLOR }] : [];

const lossWidthsOf = ({
  netContributed,
  retainedInterest,
  retainedCashback,
}: CompositionWidthsParams): CompositionWidths => {
  const cashback = Math.max(retainedCashback, 0);
  const base = netContributed + cashback;
  const lossWidth =
    base > 0 ? Math.min((Math.abs(retainedInterest) / base) * 100, 100) : 100;
  const cashbackWidth =
    base > 0 ? Math.min((cashback / base) * 100, 100 - lossWidth) : 0;
  const netWidth = 100 - lossWidth - cashbackWidth;
  return {
    segments: [
      { width: netWidth, color: NET_COLOR },
      { width: lossWidth, color: LOSS_COLOR },
      ...cashbackSegmentOf(retainedCashback, cashbackWidth),
    ],
    netPercent: netWidth,
    interestPercent: lossWidth,
    cashbackPercent: cashbackWidth,
  };
};

const gainWidthsOf = ({
  netContributed,
  retainedInterest,
  retainedCashback,
}: CompositionWidthsParams): CompositionWidths => {
  const cashback = Math.max(retainedCashback, 0);
  const base = netContributed + retainedInterest + cashback;
  if (base <= 0) {
    return {
      segments: [],
      netPercent: 100,
      interestPercent: 0,
      cashbackPercent: 0,
    };
  }
  const netWidth = (netContributed / base) * 100;
  const cashbackWidth = (cashback / base) * 100;
  const interestWidth = 100 - netWidth - cashbackWidth;
  return {
    segments: [
      { width: netWidth, color: NET_COLOR },
      { width: interestWidth, color: INTEREST_COLOR },
      ...cashbackSegmentOf(retainedCashback, cashbackWidth),
    ],
    netPercent: netWidth,
    interestPercent: interestWidth,
    cashbackPercent: cashbackWidth,
  };
};

export const buildCompositionWidths = (
  params: CompositionWidthsParams,
): CompositionWidths =>
  params.isLoss ? lossWidthsOf(params) : gainWidthsOf(params);
